import { awsImage, compareValues } from '@firsttable/functions';
import type { FormattedMenuImage, MenuPageSourceConnection, PhotoSwipeSourceImage } from './types';

/**
 * Get the size of the menu image otherwise applies defaults todo resize large images proportionally within a max width or height dependent on orientation
 */
export const formatMenuImage = ({
  originalWidth,
  originalHeight,
  url,
  page,
}: PhotoSwipeSourceImage): FormattedMenuImage => {
  // Fall back to A5 defaults when the API omits dimensions; `??` (not a destructuring default) so explicit `null` values are handled, avoiding NaN/Infinity ratios.
  const w = originalWidth ?? 1500;
  const h = originalHeight ?? 1070;
  const a5w = 2480;
  const a5h = 1748;
  const ratio = w / h;
  const a5Ratio = a5w / a5h;

  const [width, height] =
    ratio > a5Ratio ? [a5w, Math.round(a5w / ratio)] : [Math.round(a5h * ratio), a5h];

  // inline orientation
  let orientation: 'landscape' | 'portrait' | 'even';
  if (w > h) {
    orientation = 'landscape';
  } else if (w < h) {
    orientation = 'portrait';
  } else {
    orientation = 'even';
  }

  return {
    src: awsImage(url, width, height),
    url,
    w,
    h,
    orientation,
    page: page ?? undefined,
  };
};

export const getMenuPages = (menuImages: MenuPageSourceConnection): FormattedMenuImage[] => {
  // 1. Build PDF pages (each edge → array of images)
  const pdfPages = menuImages.edges
    .filter(({ node }) => node.isPDF ?? node.extension === 'pdf')
    .map(({ node }) =>
      (node.menuImages?.edges ?? [])
        .map((e) => e.node)
        .filter(Boolean)
        .map(formatMenuImage)
    )
    .filter((page) => page.length > 0);

  // 2. Collect single (non-PDF) pages
  const singlePages = menuImages.edges
    .filter(({ node }) => !(node.isPDF ?? node.extension === 'pdf'))
    .map(({ node }) => node.menuImages?.edges?.[0]?.node)
    .filter(Boolean)
    .map(formatMenuImage);

  // 3. Combine and sort each page group by "page"
  const pages = [...pdfPages];
  if (singlePages.length) pages.push(singlePages);
  pages.forEach((group) => group.sort(compareValues('page')));

  return pages.flat();
};

/**
 * Find the maximum lastEdited datetime
 */
export const findMaxLastEdited = (menuImages?: MenuPageSourceConnection | null) =>
  (menuImages?.edges || []).reduce((max, { node }) => {
    const lastEdited = node?.lastEdited || '';
    return lastEdited > max ? lastEdited : max;
  }, '');
